// =============================================================================
// externalReceiptPallets — the one rule about pallets that arrived already
// packed, in a place both screens that need it can import.
//
// `receive_packed_pallets` (mig 20260807090000) parks an external receipt on a
// SYNTHETIC run: run_kind 'EXTERNAL_RECEIPT', COMPLETE at birth, run_number =
// the RCV- receipt number. Its pallets are real pallets in every other respect
// — they weigh, cool, inspect, dispatch and bill — with one exception:
//
//   pallets.product_id is NULL, for life.
//
// That column is not just a billing tag; it is the rate-card key the packaging
// auto-issue triggers read (issue_pallet_stock on INSERT,
// resync_pallet_stock_on_carton_edit on UPDATE). Fruit that arrived packed
// consumed none of our cartons, so §4 of that migration installs
// pallets_block_external_product_code — BEFORE INSERT OR UPDATE **OF
// product_id** — which raises 23514 the moment a non-NULL value is written.
//
// The screens did not know. PalletDetail's admin edit auto-resolved a variant
// off the pallet's sender-supplied mark and REQUIRED one before it would save,
// then sent it — so a received pallet could not be edited at all, and the
// operator got a generic "a value doesn't pass one of this record's validation
// rules" (kf49, 2026-09-10). Unbilled pallets listed the same pallets as
// missing a code and invited a bulk re-code the DB refused the same way.
//
// Two exports, because the rule has two halves: recognise the run, and keep
// the column out of the patch. `palletProductPatch` returns a SPREADABLE
// fragment rather than a value on purpose — the trigger fires on the column
// being NAMED, so "write NULL" is not the same as "don't write it", and the
// stored value must be left exactly as it is.
// =============================================================================

export const EXTERNAL_RECEIPT_RUN_KIND = 'EXTERNAL_RECEIPT'

/** True when this run is the synthetic one minted by an external receipt. */
export function isExternalReceiptRun(runKind: string | null | undefined): boolean {
  return runKind === EXTERNAL_RECEIPT_RUN_KIND
}

/**
 * The `product_id` fragment of a pallets patch — spread it into the object
 * handed to updateWithVersion. Empty for a received pallet, so the column is
 * never named and the guard trigger never sees the write.
 */
export function palletProductPatch(
  productId: string | null,
  isExternalReceipt: boolean,
): { product_id?: string | null } {
  return isExternalReceipt ? {} : { product_id: productId }
}

/**
 * A pallet's own variety off its `fruit_tokens(variety)` embed, or null. The
 * embed is to-one, but PostgREST may hand it back as a one-element array; a
 * blank name counts as none. A received pallet needs this because its run's
 * variety is the receipt's ('MIXED' when the receipt held several).
 */
export function tokenVarietyOf(
  token: { variety: string | null } | { variety: string | null }[] | null | undefined,
): string | null {
  const t = Array.isArray(token) ? token[0] : token
  return t?.variety?.trim() || null
}
